import useConversation from '@hooks/useConversation'
import useReduxAction from '@hooks/useReduxAction'
import useSelector from '@hooks/useSelector'

import type { EntityOptions } from '@redux/modules/types'

type UseFindSubjectConversationParams = {
  conversation: {
    subject_id?: number,
    subject_type?: string,
    title?: string,
  },
}

function useFindSubjectConversation(params: UseFindSubjectConversationParams) {
  const {
    conversation: conversationParams,
    conversation: {
      subject_id: subjectId,
      subject_type: subjectType,
    },
  } = params

  const conversations = useSelector((reduxState) => {
    const array = Object.values(reduxState.entities.conversations)
    const filtered = array.filter((conversation) => {
      const { subject_id, subject_type } = conversation
      return subject_type === subjectType && subject_id === subjectId
    })

    return filtered
  })

  const conversation = conversations[0] || conversationParams

  return {
    conversation,
    hasMultipleConversations: conversations.length > 1,
  }
}

type UseSubjectConversationOptions = {
  conversation: {
    subject_id?: number | undefined,
    subject_type?: string,
    title?: string,
  },
  performHttpRequests?: boolean,
  publicCommentsOnly?: boolean,
  requestOptions?: EntityOptions,
}

function useSubjectConversation(options: UseSubjectConversationOptions) {
  const {
    conversation: conversationParams,
    conversation: {
      subject_id: subjectId,
      subject_type: subjectType,
    },
    performHttpRequests,
    requestOptions = {},
  } = options

  const subjectConversationPayload = useFindSubjectConversation({
    conversation: conversationParams,
  })
  const { conversation } = subjectConversationPayload

  const conversationPayload = useConversation(conversation, options)

  const { loading } = useSelector(reduxState => reduxState.conversations)

  // Load Conversation
  const entityKey = `${subjectType}${subjectId}`

  useReduxAction(
    'conversations',
    'loadConversationForSubject',
    requestOptions,
    [subjectType, subjectId, performHttpRequests],
    {
      dispatchAction: (action, actionRequestOptions) => action(subjectType, subjectId, actionRequestOptions),
      shouldPerformFn: (entityReducer) => {
        const { loadedForKeys } = entityReducer
        return performHttpRequests && subjectType && subjectId && !loadedForKeys.includes(entityKey)
      },
    },
  )

  const hasConversation = !!conversation.id

  return {
    ...conversationPayload,
    hasConversation,
    loading,
    subjectType,
    subjectId,
  }
}

export default useSubjectConversation
